import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from 'react-router'
import styled from 'styled-components'

import * as sessionUserSelector from 'selectors/sessionUser'
import { userTimeZoneFormat } from 'utils/time'
import { RouteTicketsSearchResults } from 'routes'

import Sidebar from 'components/Shared/Sidebar/RightSidebar'
import SidebarSection from 'components/Shared/Sidebar/SidebarSection/Index'
import SidebarSectionJson from 'components/Shared/Sidebar/SidebarSectionJson/Index'
import SidebarText from 'components/Shared/Sidebar/SidebarText/Index'
import SidebarSectionGroup from 'components/Shared/Sidebar/SidebarSectionGroup/Index'
import SidebarSectionGroupTitle from 'components/Shared/Sidebar/SidebarSectionGroupTitle/Index'
import Table from 'components/Shared/TableNew/Index'
import TableBody from 'components/Shared/TableNew/TableBody'
import TableRow from 'components/Shared/TableNew/TableRow'
import TableCell from 'components/Shared/TableNew/TableCell'
import ScrollArea from 'components/Shared/ScrollArea/Index'

const StyledScrollArea = styled(ScrollArea)`
  max-height: 120px;
`

const PartnerAppRequestsSidebar = ({ request, onClose, ticketingSystemName }) => {
  const { t } = useTranslation()
  const userTimeZone = useSelector(sessionUserSelector.timezone)

  if (!request) {
    return null
  }

  const fullCopyText = JSON.stringify(request, null, 2)
  const respondedAt = userTimeZoneFormat(userTimeZone, request.finishedAt)

  const ticketExternalIds = () => {
    return (
      <StyledScrollArea>
        <Table width='100%'>
          <TableBody hoverable={false}>
            {request.ticketExternalIds.map((id) => {
              const linkTo = `${RouteTicketsSearchResults()}?search=${id}`

              return (
                <TableRow key={id}>
                  <TableCell>
                    <Link to={linkTo}>{id}</Link>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </StyledScrollArea>
    )
  }

  return (
    <Sidebar
      key={request.id}
      onClose={onClose}
      title={t('appRequestsPage.sidebarDetails')}
      copyText={fullCopyText}
      dataTest='request-details'
    >
      <SidebarSection title={t('appRequestsPage.sidebarSectionUrl')} copyText={request.url}>
        <SidebarText text={request.url} />
      </SidebarSection>

      <SidebarSection
        title={t('appRequestsPage.sidebarSectionRequestId')}
        copyText={request.requestId}
      >
        <SidebarText text={request.requestId} />
      </SidebarSection>

      <SidebarSection
        title={t('appRequestsPage.sidebarSectionClientId')}
        copyText={request.metadata?.clientId}
      >
        <SidebarText text={request.metadata?.clientId} />
      </SidebarSection>

      <SidebarSection
        title={t('appRequestsPage.sidebarSectionRespondedAt')}
        copyText={respondedAt}
      >
        <SidebarText text={respondedAt} />
      </SidebarSection>

      <SidebarSectionGroup>
        <SidebarSectionGroupTitle
          title={t('appRequestsPage.sidebarSectionTs')}
        />

        <SidebarSection
          title={t('appRequestsPage.sidebarSectionTsId')}
          copyText={request.metadata?.ticketingSystemId}
        >
          <SidebarText text={request.metadata?.ticketingSystemId} />
        </SidebarSection>

        <SidebarSection title={t('appRequestsPage.sidebarSectionTsName')}>
          <SidebarText text={ticketingSystemName} />
        </SidebarSection>

      </SidebarSectionGroup>

      <SidebarSection
        title={t('appRequestsPage.sidebarSectionTicketIds')}
        copyText={request.ticketExternalIds?.toString()}
      >
        {ticketExternalIds()}
      </SidebarSection>

      <SidebarSectionJson
        title={t('appRequestsPage.sidebarSectionReqPayload')}
        json={request.payload}
      />

      <SidebarSection title={t('appRequestsPage.sidebarSectionReqPayloadSize')}>
        <SidebarText text={request.payloadSize} />
      </SidebarSection>

      <SidebarSectionJson
        title={t('appRequestsPage.sidebarSectionRespPayload')}
        json={request.responsePayload}
      />

      <SidebarSection title={t('appRequestsPage.sidebarSectionSourceIp')} copyText={request.ip}>
        <SidebarText text={request.ip} />
      </SidebarSection>
    </Sidebar>
  )
}

PartnerAppRequestsSidebar.propTypes = {
  request: PropTypes.object,
  ticketingSystemName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
}

export default PartnerAppRequestsSidebar
